import React from "react";
import { motion } from "framer-motion";
import {
  ShieldCheck,
  CheckCircle,
  Award,
  Globe,
  Users,
  TrendingUp,
  FileCheck,
  ArrowRight,
  Mail,
  ExternalLink,
  Target,
  BarChart3,
} from "lucide-react";
import Navbar from "../../../components/Navbar";
import Footer from "../../../components/Footer";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 },
};

const ISO27701_Details = () => {
  return (
    <>
      <Navbar />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460] text-white py-24 px-4 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-orange-500 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-purple-600 rounded-full blur-3xl" />
        </div>
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="relative max-w-5xl mx-auto text-center"
        >
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-sm mb-6">
            <ShieldCheck size={18} className="text-orange-400" />
            <span>ISO Lead Auditor Training</span>
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
            ISO/IEC 27701 Certification
          </h1>
          <p className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Privacy Information Management System (PIMS) — International
            standard for managing privacy risks, protecting personal data, and
            ensuring compliance with global privacy regulations.
          </p>
        </motion.div>
      </section>

      {/* Overview Section */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              ISO/IEC 27701 Certification Overview
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <p className="text-gray-600 text-lg leading-relaxed mb-4">
              ISO/IEC 27701:2019 is the international standard for Privacy
              Information Management Systems (PIMS). It provides organizations
              with a framework for managing privacy risks, protecting personal
              data, and ensuring compliance with data protection laws such as
              GDPR (General Data Protection Regulation), CCPA (California
              Consumer Privacy Act), and other global privacy regulations.
              ISO/IEC 27701 helps businesses establish robust processes for
              handling personal information, ensuring that privacy is integrated
              into their information security management system (ISMS), as
              outlined in ISO/IEC 27001.
            </p>
            <p className="text-gray-600 text-lg leading-relaxed">
              Achieving ISO/IEC 27701 certification demonstrates an
              organization’s commitment to privacy protection and data security,
              building trust with customers, stakeholders, and regulators. The
              certification also helps businesses mitigate the risk of data
              breaches, privacy violations, and legal penalties, while enabling
              better management of privacy controls and compliance requirements.
            </p>
          </motion.div>
        </div>
      </section>

      {/* What Is ISO/IEC 27701 */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              What Is ISO/IEC 27701 Certification?
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <p className="text-gray-600 text-lg leading-relaxed mb-8">
              ISO/IEC 27701 is a privacy extension to the ISO/IEC 27001 standard
              for information security management. It provides specific
              guidelines and requirements for establishing, implementing,
              maintaining, and improving a Privacy Information Management System
              (PIMS). The standard helps organizations ensure that personal data
              is handled securely and in compliance with privacy laws and
              regulations.
            </p>
            <p className="text-gray-700 font-semibold text-lg mb-4">
              ISO/IEC 27701 covers the following key areas:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                "Privacy risk management: Identifying, assessing, and mitigating privacy risks associated with the collection, processing, and storage of personal data.",
                "Data subject rights management: Ensuring compliance with the rights of data subjects (e.g., access, rectification, erasure, and data portability).",
                "Data processing agreements: Defining the responsibilities of data controllers and processors in line with privacy regulations.",
                "Compliance with global privacy regulations: Ensuring that the organization meets the requirements of global data protection laws, including GDPR, CCPA, and others.",
              ].map((text, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.1 }}
                  className="flex items-center gap-3 bg-white p-4 rounded-xl shadow-sm border border-gray-100"
                >
                  <CheckCircle
                    size={22}
                    className="text-[var(--bg-primary)] shrink-0"
                  />
                  <span className="text-gray-700 font-medium">{text}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Key Features of ISO/IEC 27701 Certification
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mx-auto" />
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: ShieldCheck,
                title: "Privacy Management System (PIMS)",
                desc: "ISO/IEC 27701 helps businesses implement a comprehensive Privacy Information Management System (PIMS) within their existing ISMS. The PIMS framework helps integrate privacy management with information security to provide a holistic approach to managing data protection.",
              },
              {
                icon: Target,
                title: "Privacy Risk Management",
                desc: "The certification requires organizations to identify and assess privacy risks related to the collection, storage, and processing of personal data. It helps businesses implement appropriate controls and procedures to mitigate these risks and ensure data protection.",
              },
              {
                icon: Users,
                title: "Data Subject Rights and Compliance",
                desc: "ISO/IEC 27701 helps organizations manage data subject rights, such as the right to access, right to rectification, right to erasure, and right to portability. The standard also ensures compliance with privacy regulations, such as GDPR and CCPA.",
              },
              {
                icon: Globe,
                title: "Data Processor and Data Controller Roles",
                desc: "The standard defines the roles and responsibilities of data controllers and data processors and ensures that data processing agreements are in place to outline privacy and security obligations.",
              },
              {
                icon: TrendingUp,
                title: "Continuous Improvement",
                desc: "ISO/IEC 27701 promotes the continuous improvement of privacy practices by ensuring that privacy risks are regularly monitored, evaluated, and addressed. The certification supports the PDCA (Plan-Do-Check-Act) cycle to improve privacy management over time.",
              },
            ].map((feature, i) => (
              <motion.div
                key={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="group bg-gray-50 hover:bg-white p-6 rounded-2xl border border-gray-100 hover:shadow-xl transition-all duration-300"
              >
                <div className="w-12 h-12 bg-[var(--bg-primary)] rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <feature.icon size={24} className="text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-600">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Matters */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Why ISO/IEC 27701 Certification Matters for Your Business
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                "Ensure Privacy Compliance — ISO/IEC 27701 certification helps businesses comply with global privacy regulations such as GDPR, CCPA, and other regional laws, ensuring that the handling of personal data aligns with legal requirements.",
                "Build Customer Trust — Achieving ISO/IEC 27701 certification demonstrates your commitment to protecting customer privacy and personal data. It increases customer trust and confidence in your ability to safeguard their sensitive information.",
                "Mitigate Privacy Risks — By implementing a Privacy Information Management System (PIMS), organizations can better identify, assess, and mitigate privacy risks associated with the processing and storage of personal data, reducing the likelihood of data breaches and privacy violations.",
                "Competitive Advantage — ISO/IEC 27701 certification provides a competitive advantage in industries where privacy and data protection are critical. It signals to customers, partners, and regulators that your organization has implemented best practices for data privacy and security.",
                "Reduced Legal and Financial Risks — By aligning with privacy regulations and adopting robust privacy practices, businesses can reduce the risk of legal penalties, fines, and reputational damage resulting from non-compliance or data breaches.",
              ].map((point, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className="flex items-start gap-3 p-4 rounded-xl bg-white border border-gray-100"
                >
                  <ArrowRight
                    size={18}
                    className="text-[var(--bg-primary)] mt-1 shrink-0"
                  />
                  <span className="text-gray-700">{point}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* What Does Include */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              What Does ISO/IEC 27701 Certification Include?
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <p className="text-gray-600 text-lg leading-relaxed mb-6">
              ISO/IEC 27701 outlines the steps for establishing, implementing,
              and improving a Privacy Information Management System (PIMS). Key
              components of the certification include:
            </p>
            <div className="space-y-4">
              {[
                "1. Establishing Privacy Policies and Objectives — Organizations must define clear privacy policies, objectives, and governance structures to guide the management of personal data and ensure privacy compliance.",
                "2. Privacy Risk Assessment — Conduct a privacy risk assessment to identify and evaluate risks related to personal data processing, including data breaches, misuse, and non-compliance.",
                "3. Data Subject Rights Management — Implement processes for managing data subject rights, such as the right to access, rectification, erasure, and data portability, in line with GDPR and other privacy laws.",
                "4. Security Controls and Privacy Protection — Integrate information security and privacy controls to ensure the confidentiality, integrity, and availability of personal data. This may include data encryption, access controls, and incident response procedures.",
                "5. Monitoring and Auditing — Establish ongoing monitoring and auditing mechanisms to track privacy performance, identify areas for improvement, and ensure compliance with privacy regulations.",
                "6. Third-Party Data Processing Agreements — Ensure that appropriate data processing agreements are in place with third-party vendors and partners that process personal data, outlining their responsibilities for data security and privacy.",
                "7. Continual Improvement — Apply the PDCA (Plan-Do-Check-Act) cycle to continually improve privacy practices, evaluate the effectiveness of privacy controls, and ensure compliance with evolving regulations.",
              ].map((step, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  className="flex items-start gap-4 bg-gray-50 p-5 rounded-2xl shadow-sm border border-gray-100"
                >
                  <div className="w-8 h-8 bg-[var(--bg-primary)] rounded-full flex items-center justify-center shrink-0">
                    <span className="text-white font-bold text-sm">
                      {i + 1}
                    </span>
                  </div>
                  <span className="text-gray-700 text-lg">{step}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Benefits of ISO/IEC 27701 Certification
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                "Enhanced Data Privacy",
                "Regulatory Compliance",
                "Customer Confidence",
                "Improved Risk Management",
                "Competitive Advantage",
              ].map((adv, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
                >
                  <CheckCircle
                    size={20}
                    className="text-[var(--bg-primary)] mb-3"
                  />
                  <p className="text-gray-700 font-medium text-sm">{adv}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* How to Achieve */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              How to Achieve ISO/IEC 27701 Certification
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <p className="text-gray-700 font-semibold text-lg mb-6">
              Achieving ISO/IEC 27701 certification typically involves the
              following steps:
            </p>
            <div className="space-y-4">
              {[
                "1. Establish a Privacy Management Framework — Develop a Privacy Information Management System (PIMS) aligned with ISO/IEC 27701, integrating privacy management practices with your existing ISMS (if applicable).",
                "2. Conduct a Privacy Risk Assessment — Identify and assess the privacy risks associated with personal data processing, evaluating potential threats and vulnerabilities that could impact data security and privacy.",
                "3. Implement Privacy Controls — Implement the necessary privacy controls and procedures to mitigate identified risks and ensure compliance with data protection laws.",
                "4. Internal Audits and Documentation — Conduct internal audits to evaluate the effectiveness of your PIMS and document your privacy management practices to demonstrate compliance during audits.",
                "5. Engage a Certification Body — Work with an accredited third-party certification body to assess your PIMS and verify compliance with ISO/IEC 27701.",
                "6. Continuous Monitoring and Improvement — Continuously monitor the effectiveness of your privacy controls and practices, making adjustments as necessary to address emerging privacy risks and regulatory requirements.",
              ].map((step, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  className="flex items-start gap-4 bg-gray-50 p-5 rounded-2xl shadow-sm border border-gray-100"
                >
                  <div className="w-8 h-8 bg-[var(--bg-primary)] rounded-full flex items-center justify-center shrink-0">
                    <span className="text-white font-bold text-sm">
                      {i + 1}
                    </span>
                  </div>
                  <span className="text-gray-700 text-lg">{step}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Choose MBA Solutions */}
      <section className="py-16 px-4 bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460] text-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Why Choose MBA Solutions for ISO/IEC 27701 Certification?
            </h2>
            <div className="w-20 h-1 bg-orange-500 rounded-full mx-auto mb-6" />
            <p className="text-gray-300 text-lg leading-relaxed max-w-4xl mx-auto">
              MBA Solutions Pvt. Ltd. provides expert ISO/IEC 27701
              certification consultancy services, helping businesses develop and
              implement a comprehensive Privacy Information Management System
              (PIMS) that ensures data protection and regulatory compliance.
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mb-10"
          >
            <p className="text-gray-300 font-semibold text-lg text-center mb-6">
              Our services include:
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {[
                "Gap Analysis and Risk Assessment",
                "Privacy Policy and Procedure Development",
                "Privacy Risk Treatment and Control Implementation",
                "ISMS and PIMS Integration",
                "Internal Audit and Pre-certification Support",
                "Third-Party Certification Assistance",
                "Ongoing Monitoring, Reporting, and Continuous Improvement",
              ].map((service, i) => (
                <span
                  key={i}
                  className="bg-white/10 backdrop-blur-sm px-5 py-2 rounded-full text-sm font-medium"
                >
                  {service}
                </span>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 md:p-10 text-center"
          >
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mb-6">
              <a
                href="mailto:atif@mbasolutions.co"
                className="flex items-center gap-2 text-orange-400 hover:text-orange-300 transition-colors"
              >
                <Mail size={20} />
                <span>atif@mbasolutions.co</span>
              </a>
              <a
                href="https://www.mbasolutions.co"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-orange-400 hover:text-orange-300 transition-colors"
              >
                <ExternalLink size={20} />
                <span>www.mbasolutions.co</span>
              </a>
            </div>
            <p className="text-gray-300 text-base leading-relaxed max-w-3xl mx-auto">
              We help businesses achieve ISO/IEC 27701 certification, ensuring
              data privacy, security, and regulatory compliance.
            </p>
          </motion.div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default ISO27701_Details;
